"use client";

import React from "react";
import { Phone, Clock, Mail, CircleCheck, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";
export default function SupportUs() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="1794" id="consultation-portal" ref={ref} className="bg-background py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className={cn("flex flex-col lg:flex-row items-center lg:items-stretch transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          
          <div className="w-full lg:w-7/12 bg-gradient-to-br from-[#095fa3] to-[#074b82] text-white rounded-xl p-8 sm:p-12 lg:p-16 lg:pr-32 flex flex-col justify-center relative z-0 shadow-lg">
            <div className="space-y-8">
              <div>
                <p className="text-sm font-bold uppercase tracking-widest text-[#fb8c26] mb-4">
                  CONTACT US
                </p>
                <h2 className="text-4xl sm:text-5xl font-bold font-sans mb-6 leading-tight tracking-tight">
                  Modernize Your Agency Today
                </h2>
                <p className="text-white/90 text-xl font-sans leading-relaxed">Partner with us to transform your governmental operations with our industry-leading ERP solutions. Schedule a free technical consultation.</p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-white/10 p-3 rounded-md shrink-0">
                    <Phone className="size-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 font-medium uppercase tracking-wider">
                      Direct Support Line
                    </p>
                    <Link to="tel:18004683771" className="text-2xl font-bold hover:text-[#fb8c26] transition-colors block mt-1">+1 720-772-6420</Link>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-white/10 p-3 rounded-md shrink-0">
                    <Clock className="size-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 font-medium uppercase tracking-wider">
                      Operational Hours
                    </p>
                    <p className="text-lg font-medium mt-1">Mon-Fri: 9:00 AM - 5:00 PM MST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-white/10 p-3 rounded-md shrink-0">
                    <Mail className="size-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 font-medium uppercase tracking-wider">
                      Procurement Email
                    </p>
                    <p className="text-lg font-medium mt-1">info@kanha.biz</p>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-white/20">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  <p className="flex items-center gap-2 text-sm text-white/90">
                    <CircleCheck className="size-4 text-[#fb8c26]" />
                    <span>White-Glove Onboarding</span>
                  </p>
                  <p className="flex items-center gap-2 text-sm text-white/90">
                    <CircleCheck className="size-4 text-[#fb8c26]" />
                    <span>FedRAMP Compliance Ready</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          
          <div className="w-full lg:w-5/12 mt-8 lg:mt-0 lg:-ml-20 relative z-10 flex items-center">
            <div className="bg-white dark:bg-card w-full rounded-xl shadow-2xl p-8 sm:p-10 border border-border/50">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-[#3f444b] dark:text-foreground font-sans tracking-tight">Schedule a Free Consultation</h3>
                <p className="text-muted-foreground text-sm mt-1">Fill out the form below and our specialists will reach out shortly.</p>
              </div>

              <FormProvider formId="consultation_portal_form_home" sectionName="professional-consultation" defaultValues={{
              name: "",
              email: "",
              phone: "",
              organization: "",
              professional_role: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              organization: {
                required: true
              }
            }} submitText="Schedule Consultation" successMessage="Thank you! Your consultation request has been received. A GovERP specialist will contact you shortly.">
                <ConsultationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const {
    getValues
  } = useFormContext();
  const handleExtendedSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await handleSubmit();
    const values = getValues();
    if (!values.name || !values.email) return;
    const emailBody = `
Full Name: ${values.name}
Email Address: ${values.email}
Phone Number: ${values.phone}
Organization / Agency: ${values.organization}
Professional Role: ${values.professional_role}
Brief Description: ${values.message}

Submission Date/Time: ${new Date().toLocaleString()}
    `.trim();
    try {
      await (window as any).wvcClient.sendEmail({
        to: "solutions@goverp.io",
        subject: "New Consultation Request - GovERP Website",
        body: emailBody
      });
    } catch (error) {
      console.error("Failed to send consultation request email:", error);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="bg-green-100 dark:bg-green-900/30 p-4 rounded-full">
          <CircleCheck className="size-12 text-green-600 dark:text-green-400" />
        </div>
        <h4 className="text-xl font-bold text-foreground">Request Received</h4>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-[#095fa3] text-[#095fa3] rounded-[4px]">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleExtendedSubmit} className="space-y-4">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Full name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" className="border-[#e0e0e0] focus:ring-[#095fa3] rounded-[4px]" {...field} />
            </FormControl>
            <FormDescription>Enter the name of the person requesting the consultation.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="border-[#e0e0e0] focus:ring-[#095fa3] rounded-[4px]" {...field} />
              </FormControl>
              <FormDescription>We'll use this to send your consultation details.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-14567" className="border-[#e0e0e0] focus:ring-[#095fa3] rounded-[4px]" {...field} />
              </FormControl>
              <FormDescription>Include country code for international callers.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Organization / Agency</FormLabel>
              <FormControl>
                <Input placeholder="Department of Finance" className="border-[#e0e0e0] focus:ring-[#095fa3] rounded-[4px]" {...field} />
              </FormControl>
              <FormDescription>Name of the government entity you represent.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="professional_role" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Professional Role</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full border-[#e0e0e0] focus:ring-[#095fa3] rounded-[4px]">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Agency Director">Agency Director</SelectItem>
                  <SelectItem value="IT Administrator">IT Administrator</SelectItem>
                  <SelectItem value="Compliance Officer">Compliance Officer</SelectItem>
                  <SelectItem value="Other Stakeholder">Other Stakeholder</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Brief Description</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your ERP needs or specific questions..." className="border-[#e0e0e0] focus:ring-[#095fa3] rounded-[4px] min-h-[100px]" {...field} />
            </FormControl>
            <FormDescription>The more details you provide, the better we can prepare.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" className="w-full h-12 text-base font-bold shadow-md bg-[#fb8c26] hover:bg-[#e07b1f] text-white rounded-[4px] transition-all duration-300" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </> : "Schedule Consultation"}
      </Button>
    </Form>;
}